"use client";

import React from "react";

import { XIcon } from "lucide-react";

import { cn } from "@shared/lib/style";

import * as SheetPrimitive from "@radix-ui/react-dialog";

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetOverlay({
  className,
  animate = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay> & {
  animate?: boolean;
}) {
  return (
    <SheetPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-black/50",
        animate &&
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      data-slot="sheet-overlay"
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  hideClose = false,
  animate = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  hideClose?: boolean;
  animate?: boolean;
}) {
  return (
    <SheetPortal>
      <SheetOverlay animate={animate} />
      <SheetPrimitive.Content
        className={cn(
          "bg-background fixed z-50 flex flex-col gap-4 shadow-lg",
          animate &&
            "data-[state=open]:animate-in data-[state=closed]:animate-out transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
          side === "right" &&
            cn(
              "inset-y-0 right-0 h-full w-3/4 sm:max-w-sm",
              animate &&
                "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
            ),
          side === "left" &&
            cn(
              "inset-y-0 left-0 h-full w-3/4 sm:max-w-sm",
              animate &&
                "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
            ),
          side === "top" &&
            cn(
              "inset-x-0 top-0 h-auto",
              animate &&
                "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
            ),
          side === "bottom" &&
            cn(
              "inset-x-0 bottom-0 h-auto",
              animate &&
                "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            ),
          className,
        )}
        data-slot="sheet-content"
        {...props}
      >
        {!hideClose && (
          <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary top-4.5 rounded-xs focus:outline-hidden absolute left-5 cursor-pointer opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-transparent focus:ring-offset-2 disabled:pointer-events-none">
            <XIcon className="size-5" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}
function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex flex-col gap-1.5 p-4", className)}
      data-slot="sheet-header"
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      data-slot="sheet-footer"
      {...props}
    />
  );
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      className={cn("text-foreground font-semibold", className)}
      data-slot="sheet-title"
      {...props}
    />
  );
}
function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      className={cn("text-muted-foreground text-sm", className)}
      data-slot="sheet-description"
      {...props}
    />
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
